import {A360ProductCd, A360ProductModel} from 'Quoting/models/Quoting';
import {isDefined} from 'Quoting/utils/utilities';
import {Either, isLeft, left, right} from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {FieldValidationRules} from '../types';
import {HIValidationRules} from './HIMaster.rules';
import {CIValidationRules} from './CIMaster.rules';
import {ACCValidationRules} from './ACCMaster.rules';
import {GPLUValidationRules} from './GPLUMaster.rules';

const isRequired = (value: any, errorMessage: string): Either<string, void> =>
  isDefined(value) ? right(undefined) : left(errorMessage);

const isInRange = (value: number, min: number, max: number, errorMessage: string): Either<string, void> =>
  isDefined(value) && value >= min && value <= max ? right(undefined) : left(errorMessage);

const isStep = (value: number, rules: any): Either<string, void> =>
  isDefined(value) && value % rules.step === 0
    ? isInRange(
        value,
        rules.min,
        rules.max,
        rules.errorMessages.minMax ?? `The value must be between ${rules.min} and ${rules.max}`
      )
    : left(rules.errorMessages.minMax);

const isAllowed = (value: number, allowedArray: Array<number>, errorMessage: string): Either<string, void> =>
  isDefined(value) && allowedArray.includes(value) ? right(undefined) : left(errorMessage);

export const validateField = (key: string, value: any, validationRulset: Record<string, FieldValidationRules>) => {
  const rules = validationRulset[key];

  if (!rules) return right(undefined);

  return pipe(isRequired(value, rules.errorMessages.required ?? 'Value is required'), result =>
    isLeft(result)
      ? result
      : pipe(value, v => {
          if (rules.allowedValues) {
            return pipe(
              isAllowed(
                Number.parseInt(value) ?? 0,
                rules.allowedValues,
                rules.errorMessages.allowedValues ??
                  `Acceptable Amount: ${rules.allowedValues
                    .map(val => `$${val.toLocaleString(navigator.language, {minimumFractionDigits: 0})}`)
                    .toString()}`
              )
            );
          } else if (typeof v === 'number') {
            if (rules.step) {
              return pipe(isStep(v, rules));
            }
            return pipe(
              isInRange(
                v,
                rules.min ?? 0,
                rules.max ?? 0,
                rules.errorMessages.minMax ?? `The value must be between ${rules.min} and ${rules.max}`
              )
            );
          }
          return right(undefined);
        })
  );
};

export const getFieldError = (key: string, value: any, validationRulset: Record<string, FieldValidationRules>) => {
  const validationError = validateField(key, value, validationRulset);
  return isLeft(validationError) ? validationError.left : undefined;
};

export const getValidationRuleset = (
  productCd: A360ProductCd | undefined,
  validationVal?: {attributeName: string; amount: number}
) => {
  switch (productCd) {
    case A360ProductCd.HI:
      return HIValidationRules;
    case A360ProductCd.ACC:
      return ACCValidationRules;
    case A360ProductCd.CI:
      return CIValidationRules(validationVal);
    case A360ProductCd.GPLU:
      return GPLUValidationRules;
    default:
      return {};
  }
};

export const getDeclinedSicCodesForProduct = (productModel: A360ProductModel) => {
  switch (productModel) {
    case A360ProductModel.ACCMaster:
    case A360ProductModel.CIMaster:
    case A360ProductModel.HIMaster:
    case A360ProductModel.GPLUMaster:
      return ['3292', '8811'];
 
    default:
      return [];
  }
};
